/**
 * Racks edit body for the descriptor engine (#997 Story 6.3 R6).
 *
 * Winston's ADR (parity by identity): the engine renders the EXISTING bespoke
 * `RackEditFormContent` — the user-approved Racks editor (field set, order,
 * labels incl. UOM-aware, placeholders, controls, two-column grid, card chrome,
 * Additional-Info help) — bound to the engine's edit-session state. This body is
 * the peer of `SmallUpsInstanceEditFormBody` etc. behind the same
 * `renderEditFormContent` seam; the engine owns list/save/flush/attachment
 * lifecycle (the 6.1/6.2 fixes), the section owns its view.
 *
 * Binding contract (locked by racks-instance-edit-form-body.test.tsx):
 * - `formData` is RACK-shaped: the engine's editor field map (numbers as text,
 *   yesNo as 'Yes'/'No') is coerced back through `descriptorFieldMapToPatch`,
 *   plus identity (`rackName` ← `instanceName`, `rackStatus` ← `instanceStatus`)
 *   and the unit id (`id` ← `configurationId`, which RackEditFormContent uses to
 *   scope its per-unit attachment cards — 6.1).
 * - `updateField` routes identity keys to the engine identity setters and every
 *   data field to `setInstanceField`, converting bespoke rack-typed values to
 *   the engine's editor forms (number → text, undefined → '', boolean kept).
 */

import React, { useCallback, useMemo } from 'react';

import { RackEditFormContent } from '../../../../components/racks/RackEditFormContent';
import { descriptorFieldMapToPatch } from '../../../sections/descriptorEngineAdapter';
import { RACKS_DESCRIPTOR } from '../../../sections/racks.descriptor';

import type { RepeatableInstanceEditFormRenderProps } from '../../../../components/repeatableSections/repeatableInstanceEditFormTypes';
import type { RackConfiguration, RackStatus } from '../../../../types/rack';

function normalizeRackStatus(value: string | undefined): RackStatus {
  return value === 'existing' ? 'existing' : 'new';
}

export function RacksInstanceEditFormBody({
  instanceFields,
  setInstanceField,
  instanceName,
  instanceStatus,
  setInstanceName,
  setInstanceStatus,
  disabled,
  requestStage,
  showFullDetails,
  requestId,
  configurationId,
  attachmentsBySection,
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles,
  onFilesSelected,
  offlineMode,
  onCaptionChange,
  pendingCaptionEdits,
  markDirty,
}: RepeatableInstanceEditFormRenderProps): React.ReactElement {
  const toPatch = useMemo(
    () => descriptorFieldMapToPatch(RACKS_DESCRIPTOR),
    [],
  );

  const formData = useMemo((): Partial<RackConfiguration> => {
    const {
      instanceName: patchName,
      instanceStatus: patchStatus,
      ...dataFields
    } = toPatch(instanceFields);
    return {
      ...(dataFields as Partial<RackConfiguration>),
      id: configurationId,
      rackName: instanceName ?? (patchName as string | undefined),
      rackStatus: normalizeRackStatus(
        instanceStatus ?? (patchStatus as string | undefined),
      ),
    };
  }, [toPatch, instanceFields, configurationId, instanceName, instanceStatus]);

  const updateField = useCallback(
    <K extends keyof RackConfiguration>(
      field: K,
      value: RackConfiguration[K],
    ) => {
      if (field === 'rackName') {
        const name = typeof value === 'string' ? value : '';
        if (setInstanceName) {
          setInstanceName(name);
        } else {
          setInstanceField('instanceName', name);
        }
        return;
      }
      if (field === 'rackStatus') {
        const status = normalizeRackStatus(
          typeof value === 'string' ? value : undefined,
        );
        if (setInstanceStatus) {
          setInstanceStatus(status);
        } else {
          setInstanceField('instanceStatus', status);
        }
        return;
      }
      // The engine session stores editor forms: booleans stay booleans (yesNo),
      // numbers become text, a cleared number becomes blank — never 'undefined'.
      const editorValue =
        typeof value === 'boolean' ? value : value == null ? '' : String(value);
      setInstanceField(field as string, editorValue);
    },
    [setInstanceField, setInstanceName, setInstanceStatus],
  );

  const onFilesSelectedDirty = useMemo(
    () =>
      onFilesSelected
        ? (sectionId: string, files: Parameters<typeof onFilesSelected>[1]) => {
            markDirty();
            onFilesSelected(sectionId, files);
          }
        : undefined,
    [markDirty, onFilesSelected],
  );

  return (
    <RackEditFormContent
      formData={formData}
      updateField={updateField}
      disabled={disabled}
      requestStage={requestStage}
      requestId={requestId}
      showFullDetails={showFullDetails}
      attachmentsBySection={attachmentsBySection}
      onAttachFiles={onAttachFiles}
      onDeleteAttachment={onDeleteAttachment}
      selectedFiles={selectedFiles}
      onFilesSelected={onFilesSelectedDirty}
      isOffline={offlineMode}
      onCaptionChange={onCaptionChange}
      pendingCaptionEdits={pendingCaptionEdits}
    />
  );
}
